import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Video, Save, Loader2, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_VIDEO_ID = 'aMKXcXAz9N4';

const SiteVideosManagement = () => {
  const [welcomeId, setWelcomeId] = useState('');
  const [introId, setIntroId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [popupEnabled, setPopupEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/site-videos`);
      setWelcomeId(res.data?.welcomeVideoId || DEFAULT_VIDEO_ID);
      setIntroId(res.data?.introVideoId || DEFAULT_VIDEO_ID);
      setPopupEnabled(res.data?.welcomePopupEnabled !== false);
    } catch (e) {
      setWelcomeId(DEFAULT_VIDEO_ID);
      setIntroId(DEFAULT_VIDEO_ID);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/site-videos`, {
        welcomeVideoUrl: welcomeId,
        introVideoUrl: introId,
        welcomePopupEnabled: popupEnabled,
      });
      setWelcomeId(res.data?.welcomeVideoId || welcomeId);
      setIntroId(res.data?.introVideoId || introId);
      toast({ title: 'Saved!', description: 'Settings updated. Hard-refresh the homepage to verify.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetBoth = (videoId) => {
    setWelcomeId(videoId);
    setIntroId(videoId);
  };

  const renderPreview = (id) => {
    if (!id) return null;
    return (
      <div className="mt-2 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-[9/16] max-w-[200px]">
        <iframe
          src={`https://www.youtube.com/embed/${id}?rel=0`}
          title="Preview"
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-600" />
            Homepage Videos
          </CardTitle>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Change the YouTube videos shown in the **Welcome Popup** (Landing) and the **Introduction Video** section (Landing + /packages).
            Paste a full YouTube URL (any format — Shorts, watch, embed) or just the 11-char ID.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading current videos…
            </div>
          ) : (
            <>
              {/* Welcome popup ON/OFF toggle */}
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-800">Welcome Video Popup</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">Turn the auto-playing welcome video popup ON or OFF for new visitors.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPopupEnabled((v) => !v)}
                  data-testid="admin-popup-toggle"
                  role="switch"
                  aria-checked={popupEnabled}
                  className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${popupEnabled ? 'bg-green-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${popupEnabled ? 'translate-x-7' : ''}`} />
                </button>
              </div>

              {/* Welcome popup video */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Welcome Popup Video</Label>
                <Input
                  value={welcomeId}
                  onChange={(e) => setWelcomeId(e.target.value)}
                  placeholder="https://youtube.com/shorts/... or 11-char ID"
                  data-testid="admin-welcome-video-input"
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Plays automatically when a new visitor lands on the homepage.
                </p>
                {renderPreview(welcomeId)}
              </div>

              {/* Intro video */}
              <div className="space-y-2 border-t border-slate-200 pt-5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Introduction Video</Label>
                <Input
                  value={introId}
                  onChange={(e) => setIntroId(e.target.value)}
                  placeholder="https://youtube.com/shorts/... or 11-char ID"
                  data-testid="admin-intro-video-input"
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Embedded on the Landing page and on the /packages page.
                </p>
                {renderPreview(introId)}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-slate-200">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="admin-videos-save-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                >
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save Videos</>}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleResetBoth(DEFAULT_VIDEO_ID)}
                  data-testid="admin-videos-reset-btn"
                  className="sm:w-auto"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteVideosManagement;
